import React, { useState, useEffect, useContext } from 'react';
import { Grid2, MenuItem } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import ExpenseTextField from './ExpenseTextField';
import { DivisionEnum, TypeEnum } from './Division.enum';
import { ExpenseStatusValuesEnum } from './ExpenseStatus.enum';
import { ValidationModeContext } from '../../../contexts/VendorPaymentValidationModeContext';
import ValidationModeEnum from './ValidationMode.enum';
import { ExpenseModeEnum } from './ExpenseMode.enum';
import { BillTypeEnum } from './BillType.enum';
import { ExpenseCategoryEnum } from './ExpenseCategory.enum';

const ExpenseCategoryType = (props) => {
    const {
        expenseTypes,
        index,
        expense,
        expenseCategories,
        mode,
        isSGA,
        setIsDivisionDisabled,
        isPosted,
        isPendingSubmission,
        isViewOnly,
        isInvoiceReviewLocked = false,
        enableEditByCorrection = false,
    } = props;
    const { control, errors, watch, setValue } = useFormContext();
    const expenseCategory = watch(`expenseEntries[${index}].category`);
    const expenseBillType = watch(`expenseEntries[${index}].billType`);
    const isClinicianDeduction = expenseBillType === BillTypeEnum.ClinicianDeduction;
    const validationMode = useContext(ValidationModeContext);
    const [required, setRequired] = useState({ required: true });
    const [expenseCategoriesFiltered, setExpenseCategoriesFiltered] = useState(expenseCategories);

    useEffect(() => {
        if (mode === ExpenseModeEnum.CreditCard)
            return setRequired({
                required: isClinicianDeduction
                    ? false
                    : expense?.status === ExpenseStatusValuesEnum.Approved ||
                      expense?.status === ExpenseStatusValuesEnum.InvoiceReview,
            });

        return setRequired({
            required:
                expense?.status !== ExpenseStatusValuesEnum.NonReimbursable &&
                expense?.status !== ExpenseStatusValuesEnum.Abandoned &&
                (!isClinicianDeduction || expense?.status === ExpenseStatusValuesEnum.Approved),
        });
    }, [expense?.status, isClinicianDeduction]);

    useEffect(() => {
        if (isClinicianDeduction) {
            const filteredCategories = expenseCategories?.filter(
                (exp) => exp.category !== ExpenseCategoryEnum.Supplemental
            );
            setExpenseCategoriesFiltered(filteredCategories);
        } else {
            setExpenseCategoriesFiltered(expenseCategories);
        }
    }, [isClinicianDeduction, expenseCategories]);

    return (
        <Grid2 item size={{ xs: 12 }}>
            <Grid2 container spacing={3}>
                <Grid2 item size={{ xs: 12, md: 6 }}>
                    <Controller
                        control={control}
                        name={`expenseEntries[${index}].category`}
                        defaultValue={!expense.category ? '' : expense.category}
                        rules={{
                            required:
                                required.required &&
                                !(
                                    mode === ExpenseModeEnum.Vault &&
                                    validationMode === ValidationModeEnum.Save
                                )
                                    ? `Expense category is required ${expenseBillType}`
                                    : false,
                        }}
                        render={({ onChange, value }) => (
                            <ExpenseTextField
                                id="expense-category"
                                select
                                onChange={(e) => {
                                    if (isSGA[index]) {
                                        setValue(`expenseEntries[${index}].sgaType`, '');
                                        setValue(
                                            `expenseEntries[${index}].sgaCategory`,
                                            e.target.value
                                        );
                                    } else {
                                        setValue(`expenseEntries[${index}].bookingType`, '');
                                        setValue(
                                            `expenseEntries[${index}].bookingCategory`,
                                            e.target.value
                                        );
                                    }
                                    setValue(`expenseEntries[${index}].type`, '');
                                    onChange(e);
                                }}
                                value={!value ? '' : value}
                                label="Expense category"
                                {...required}
                                disabled={
                                    isViewOnly ||
                                    isInvoiceReviewLocked ||
                                    (isPosted && isViewOnly) ||
                                    (isPendingSubmission &&
                                        mode !== ExpenseModeEnum.Payment &&
                                        !enableEditByCorrection) ||
                                    mode === ExpenseModeEnum.Vault
                                }
                                error={Boolean(errors.expenseEntries?.[index]?.category)}
                                helperText={errors.expenseEntries?.[index]?.category?.message}
                            >
                                <MenuItem value="" disabled>
                                    Select an expense category
                                </MenuItem>
                                {!expenseCategoriesFiltered
                                    ? null
                                    : expenseCategoriesFiltered.map((option, i) => (
                                          <MenuItem
                                              key={option.category}
                                              value={option.category}
                                              id={`expense-category-menu-option-${i}`}
                                          >
                                              {option.category}
                                          </MenuItem>
                                      ))}
                            </ExpenseTextField>
                        )}
                    />
                </Grid2>
                <Grid2 item size={{ xs: 12, md: 6 }}>
                    <Controller
                        control={control}
                        name={`expenseEntries[${index}].type`}
                        defaultValue={!expense.type ? '' : expense.type}
                        rules={{
                            required:
                                required.required &&
                                !(
                                    mode === ExpenseModeEnum.Vault &&
                                    validationMode === ValidationModeEnum.Save
                                )
                                    ? 'Expense type is required'
                                    : false,
                        }}
                        render={({ onChange, value }) => (
                            <ExpenseTextField
                                id="expense-type"
                                select
                                onChange={(e) => {
                                    if (isSGA[index]) {
                                        setValue(
                                            `expenseEntries[${index}].sgaType`,
                                            e.target.value
                                        );
                                        if (e.target.value === TypeEnum.CustomerCareGiftsFlowers) {
                                            // set division to Customer Care and disable
                                            setValue(
                                                `expenseEntries[${index}].sgaDivision`,
                                                DivisionEnum.CustomerCare
                                            );
                                            setValue(
                                                `expenseEntries[${index}].division`,
                                                DivisionEnum.CustomerCare
                                            );
                                            setIsDivisionDisabled(true);
                                        } else if (
                                            e.target.value ===
                                                TypeEnum.MalpracticeCompFundStateCompfunds ||
                                            e.target.value === TypeEnum.PSYCMalpracticeCompFund
                                        ) {
                                            // set division to Malpractice and disable
                                            setValue(
                                                `expenseEntries[${index}].sgaDivision`,
                                                DivisionEnum.CustomerCare
                                            );
                                            setValue(
                                                `expenseEntries[${index}].division`,
                                                DivisionEnum.Malpractice
                                            );
                                            setIsDivisionDisabled(true);
                                        } else if (
                                            e.target.value === TypeEnum.UnidentifiedLicensing
                                        ) {
                                            // set division to CVO and disable
                                            setValue(
                                                `expenseEntries[${index}].sgaDivision`,
                                                DivisionEnum.CustomerCare
                                            );
                                            setValue(
                                                `expenseEntries[${index}].division`,
                                                DivisionEnum.CVO
                                            );
                                            setIsDivisionDisabled(true);
                                        } else if (
                                            e.target.value === TypeEnum.LodgingSecurityDeposit ||
                                            e.target.value === TypeEnum.AssociateTravelPrepaid ||
                                            e.target.value === TypeEnum.AssociateTravelAccrued ||
                                            e.target.value === TypeEnum.Disputed ||
                                            e.target.value === TypeEnum.IncidentalsRounding ||
                                            e.target.value === TypeEnum.Rebate
                                        ) {
                                            // set division to Corporate and disable
                                            setValue(
                                                `expenseEntries[${index}].sgaDivision`,
                                                DivisionEnum.CustomerCare
                                            );
                                            setValue(
                                                `expenseEntries[${index}].division`,
                                                DivisionEnum.Corporate
                                            );
                                            setIsDivisionDisabled(true);
                                        } else {
                                            setIsDivisionDisabled(false);
                                        }
                                    } else {
                                        setValue(
                                            `expenseEntries[${index}].bookingType`,
                                            e.target.value
                                        );
                                    }
                                    onChange(e);
                                }}
                                value={!value ? '' : value}
                                label="Expense type"
                                {...required}
                                disabled={
                                    isViewOnly ||
                                    isInvoiceReviewLocked ||
                                    !expenseCategory ||
                                    (isPosted && isViewOnly) ||
                                    (isPendingSubmission &&
                                        mode !== ExpenseModeEnum.Payment &&
                                        !enableEditByCorrection)
                                }
                                error={Boolean(errors.expenseEntries?.[index]?.type)}
                                helperText={errors.expenseEntries?.[index]?.type?.message}
                            >
                                <MenuItem value="" disabled>
                                    Select an expense type
                                </MenuItem>
                                {expenseTypes.map((type, i) => (
                                    <MenuItem
                                        key={type.name}
                                        value={type.name}
                                        id={`expense-type-menu-option-${i}`}
                                    >
                                        {type.description}
                                    </MenuItem>
                                ))}
                            </ExpenseTextField>
                        )}
                    />
                </Grid2>
            </Grid2>
        </Grid2>
    );
};

export default ExpenseCategoryType;
